// @flow

import zentakeApi from 'api'
import React from 'react'
import { useHistory } from 'react-router-dom'
import { toast } from 'react-toastify'
import type { Message } from 'types/message'
import type { PacketResponse } from 'types/packet-response'
import type { Response } from 'types/response'
import { ZentakeContext } from 'utils/ZentakeContext'

function useResponseDelete(
  response: ?Response,
  packetResponse: ?PacketResponse,
  message: ?Message
): {
  title: string,
  onClick: () => void,
  loading: boolean,
} {
  const history = useHistory()

  const { setAlertConfig } = React.useContext(ZentakeContext)
  const [isDeleting, setIsDeleting] = React.useState(false)

  const deleteResponse = React.useCallback(async () => {
    if (!response?.id) return

    await zentakeApi.deleteResponse(response.id)
  }, [response?.id])

  const deletePacketResponse = React.useCallback(async () => {
    if (!packetResponse?.id) return
    await zentakeApi.deletePacketResponse(packetResponse.id)
  }, [packetResponse?.id])

  const deleteMessage = React.useCallback(async () => {
    if (!message?.id) return

    await zentakeApi.deleteMessage(message?.id)
  }, [message?.id])

  const doDelete = React.useCallback(async () => {
    setIsDeleting(true)

    if (response) {
      await deleteResponse()
    } else if (packetResponse) {
      await deletePacketResponse()
    }

    if (!response?.packet_response) {
      await deleteMessage()
    }

    setIsDeleting(false)

    if (response?.packet_response) {
      history.push(
        //$FlowFixMe
        `/response/${response?.packet_response}?response_type=packet`
      )
    } else {
      history.push('/')
    }

    toast.success('Response deleted')
  }, [
    deleteMessage,
    deletePacketResponse,
    deleteResponse,
    history,
    packetResponse,
    response,
  ])

  const onDelete = React.useCallback(() => {
    setAlertConfig({
      title: 'Delete response',
      description: 'Are you sure you want to delete this response?',
      buttons: [
        { title: 'Cancel' },
        { title: 'Delete', style: 'destructive', onPress: doDelete },
      ],
      canDismiss: false,
    })
  }, [setAlertConfig, doDelete])

  return {
    title: 'Delete',
    onClick: onDelete,
    loading: isDeleting,
  }
}

export default useResponseDelete
